import {FormControl, FormField} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {Path, useWatch} from 'react-hook-form';

import {GetSalesPlatformsApiRes, TitleExtensionLanguage} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {TITLE_EXTENSION_MAX_LENGTH} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useTitleExtensionGenerateAction} from '../hooks/useTitleExtensionGenerateAction';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';
import {buildTitleLimitChips} from '../utils/buildTitleLimitChips';
import {PlatformTitleLimitChip} from './PlatformTitleLimitChip';

interface TitleFormFieldProps extends TestIdProps {
  control: FormControl<GeneralSettingsFormValues>;
  name: Path<GeneralSettingsFormValues>;
  tooltip?: string;
  maxLength?: number;
  isDisabled?: boolean;
  platforms: GetSalesPlatformsApiRes | undefined;
  vehicleId: string;
  /** Omitted for the base input, which resolves to the tenant's default content language. */
  language?: TitleExtensionLanguage;
  /** Reads this input from the form when Generate is clicked. */
  getCurrentValue: () => string;
  /** Writes the generated text into the form as a dirty value. */
  onGenerated: (text: string) => void;
  /** Disables the Generate action for a content language the suggestion API does not accept. */
  isGenerateDisabled?: boolean;
}

export function TitleFormField(props: TitleFormFieldProps) {
  // Read-only subscription — FormField below owns the controller registration.
  const watchedValue = useWatch({name: props.name, control: props.control});
  const fieldValue = typeof watchedValue === 'string' ? watchedValue : '';

  // Verified-limit platforms get their own chip; platforms riding the BE fallback
  // envelope (unverified real limit, clamped at 48 by OutboundTitleExtensionClamp)
  // collapse into a single "others" chip per envelope value.
  const chips = buildTitleLimitChips(
    (props.platforms ?? [])
      .filter((platformData) => platformData.platform.enabled && platformData.enabled)
      .map((platformData) => ({
        code: platformData.platform.code,
        title: platformData.platform.title,
        limit: platformData.platform.titleExtensionLimit,
        isLimitVerified: platformData.platform.titleExtensionLimitVerified,
      })),
    i18n.t('general.labels.others')
  );

  // The input hard-stops at the highest limit any enabled platform accepts — a longer
  // text has nowhere to go. Per-platform overruns only warn via the chips. When the
  // vehicle title eats every composed budget the BE reports 0 for those platforms, and
  // the input is still not locked — the chips carry the warning and storage accepts it.
  const highestPlatformLimit = Math.max(0, ...chips.map((chip) => chip.limit));
  const inputMaxLength =
    highestPlatformLimit > 0
      ? highestPlatformLimit
      : (props.maxLength ?? TITLE_EXTENSION_MAX_LENGTH);

  const generateActions = useTitleExtensionGenerateAction({
    vehicleId: props.vehicleId,
    language: props.language,
    getCurrentValue: props.getCurrentValue,
    isDisabled: (props.isDisabled ?? false) || (props.isGenerateDisabled ?? false),
    onGenerated: props.onGenerated,
  });

  return (
    <VStack spacing={1}>
      <FormField
        control={props.control}
        label={i18n.t('page.advertisement.labels.titleExtension')}
        name={props.name}
        type="text"
        isDisabled={props.isDisabled}
        tooltip={props.tooltip}
        maxLength={inputMaxLength}
        // Rendered inside the input rather than via `isCounterVisible`: the label row
        // already carries the Generate action, and Label's space-between would strand
        // the counter in the middle between the label and that action. Non-breaking
        // spaces because TextInput's suffix does not set noWrap — plain spaces let the
        // counter break onto a second line in a narrow column.
        suffix={`${fieldValue.length}\u00A0/\u00A0${inputMaxLength}`}
        labelActions={generateActions}
        data-testid={suffixTestId('titleExtension', props)}
      />
      <Show when={chips.length > 0}>
        <HStack spacing={3} align="center" wrap>
          {chips.map((chip) => (
            <PlatformTitleLimitChip
              key={chip.key}
              label={chip.label}
              limit={chip.limit}
              value={fieldValue}
            />
          ))}
        </HStack>
      </Show>
    </VStack>
  );
}
